import React, { FC, useCallback } from 'react'
import { TableCell, TableHead, TableRow } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { LimitedAccessRight, OrderDirection } from '../../graphql'
import { directorsStateVar } from '../../graphql/local'
import { useDetermineUserRights, usePageFiltersSorting } from '../../hooks'
import { PATH_PARAMS } from '../../routes/paths'
import { ColumnNameWithSort } from '../Common'
import { DirectorsSortBy } from './types'

const DirectorsTableHeaderComponent: FC<{
  allowToDelete?: boolean
}> = ({ allowToDelete }) => {
  const { t } = useTranslation()

  const { pageFilters, setPageFilters } = usePageFiltersSorting(directorsStateVar)
  const { sortBy, direction } = pageFilters

  const { [PATH_PARAMS.applicationId]: applicationId } = useParams() as Record<string, string>
  const { userRights } = useDetermineUserRights(applicationId)
  const isViewOnly = userRights?.limitedAccessRight === LimitedAccessRight.ViewOnly

  const handleSort = useCallback(
    (sortedBy: string) => {
      if (sortBy !== sortedBy) {
        setPageFilters({
          sortBy: sortedBy as DirectorsSortBy,
          direction: OrderDirection.Ascending,
        })
      } else {
        const sortDirection =
          direction === OrderDirection.Descending
            ? OrderDirection.Ascending
            : OrderDirection.Descending
        setPageFilters({ direction: sortDirection })
      }
    },
    [sortBy, direction],
  )

  return (
    <TableHead>
      <TableRow>
        <ColumnNameWithSort name={DirectorsSortBy.name} handleRefetch={handleSort} width="100%">
          {t('name', 'Name')}
        </ColumnNameWithSort>

        <ColumnNameWithSort name={DirectorsSortBy.role} handleRefetch={handleSort}>
          {t('role', 'Role')}
        </ColumnNameWithSort>

        <ColumnNameWithSort name={DirectorsSortBy.signingRight} handleRefetch={handleSort}>
          {t('rights', 'Rights')}
        </ColumnNameWithSort>

        <ColumnNameWithSort name={DirectorsSortBy.email} handleRefetch={handleSort}>
          {t('email', 'Email')}
        </ColumnNameWithSort>

        <ColumnNameWithSort name={DirectorsSortBy.phone} handleRefetch={handleSort}>
          {t('phoneNumber', 'Phone Number')}
        </ColumnNameWithSort>

        <TableCell width={168}>{!isViewOnly && allowToDelete && t('actions', 'Actions')}</TableCell>
      </TableRow>
    </TableHead>
  )
}

export const DirectorsTableHeader = React.memo(DirectorsTableHeaderComponent)
